import type { ApiEndpoint, NewApiEndpoint } from '../../api/monitor';
import type { ApiEndpointsController } from '../../hooks/useApiEndpoints';
import { HealthBanner } from '../../components/ui/HealthBanner';
import { ErrorCard, LoadingCard } from '../../components/ui/ScreenFallback';
import { Toast, useToast } from '../../components/ui/Toast';
import { ApiRow } from './ApiRow';
import { NewApiForm } from './NewApiForm';

/**
 * Tela de Administração: lista o que está cadastrado em `api_endpoints`, mostra
 * a saúde de cada uma (última verificação feita pelo cron do monitor) e permite
 * cadastrar e remover endpoints.
 */
export function ApisScreen({ controller }: { controller: ApiEndpointsController }) {
  const { state, operators, loading, error, checking, saving, removingId } = controller;
  const { toast, show } = useToast();

  const handleCheckNow = async () => {
    try {
      await controller.checkNow();
      show('Verificação concluída.');
    } catch (cause) {
      show((cause as Error).message, 'error');
    }
  };

  const handleCreate = async (payload: NewApiEndpoint) => {
    await controller.create(payload);
    show(`API cadastrada: ${payload.name}`);
  };

  const handleRemove = async (api: ApiEndpoint) => {
    if (!window.confirm(`Remover a API "${api.name}" do monitoramento?`)) return;

    try {
      await controller.remove(api.id);
      show(`API removida: ${api.name}`);
    } catch (cause) {
      show((cause as Error).message, 'error');
    }
  };

  if (loading) {
    return <LoadingCard message="Carregando as APIs…" />;
  }

  if (error || !state) {
    return (
      <ErrorCard
        title="Não foi possível carregar as APIs."
        detail={error}
        onRetry={() => void controller.reload()}
      />
    );
  }

  return (
    <>
      <HealthBanner state={state} checking={checking} onCheckNow={() => void handleCheckNow()} />

      <div className="border-line-200 overflow-hidden rounded-2xl border bg-white">
        {state.apis.length === 0 ? (
          <div className="text-ink-600 p-7 text-center text-[13.5px]">
            Nenhuma API cadastrada. Adicione a primeira no formulário abaixo.
          </div>
        ) : (
          state.apis.map((api) => (
            <ApiRow
              key={api.id}
              api={api}
              removing={removingId === api.id}
              onRemove={(target) => void handleRemove(target)}
            />
          ))
        )}
      </div>

      <NewApiForm operators={operators} saving={saving} onSubmit={handleCreate} />

      <Toast message={toast} />
    </>
  );
}
